import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import moment from 'moment-timezone';
import { fetchApi } from '../../Network/NetworkService';
import { exportAsExcel } from '../../Services/exportAsExcel';
import SimpleChart from '../Ui/SimpleChart';
import { notif } from '../Ui/Notification';

// Apple Pay / Google Pay versus a typed card, from our own data.
//
// Stripe puts the wallet on every charge but makes it unqueryable — no
// Dashboard filter, no API parameter — so feeli-server's webhook copies it into
// `paymentmethodusages` as the charges happen. This page reads that.

const METHOD_LABELS = {
    card: 'Carte saisie',
    apple_pay: 'Apple Pay',
    google_pay: 'Google Pay',
    link: 'Link',
    // Two different absences, from two different places. 'unknown' is what
    // feeli-server's paymentMethodOf writes when the charge is not a card at
    // all or carries no payment_method_details.type; 'inconnu' is what this
    // dashboard's own shapeByKind substitutes for a row with no method field.
    // Both can appear, so both need a label.
    unknown: 'Inconnu',
    inconnu: 'Inconnu',
};

// Which methods count as a wallet, listed positively. A blacklist of
// 'card' would fold 'unknown' — and any method enabled later, such as
// sepa_debit — into the wallet figure and overstate Apple/Google Pay.
const WALLETS = ['apple_pay', 'google_pay'];

const METHOD_COLORS = {
    card: '#1976d2',
    apple_pay: '#212121',
    google_pay: '#4caf50',
    link: '#9c27b0',
};

const labelOf = (method) => METHOD_LABELS[method] || method;
const colorOf = (method) => METHOD_COLORS[method] || '#757575';
const euros = (cents) => (cents / 100).toFixed(2);

// Europe/Paris, matching the server's buckets. toISOString() is UTC, so
// between midnight and 02:00 Paris it would default the window to end
// yesterday and silently drop the night's charges.
const PARIS = 'Europe/Paris';
const isoDay = (date) => moment.tz(date, PARIS).format('YYYY-MM-DD');

const PaymentMethods = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        start: moment.tz(PARIS).subtract(29, 'days').format('YYYY-MM-DD'),
        end: moment.tz(PARIS).format('YYYY-MM-DD'),
    });
    // The window actually fetched, which is what the figures below describe.
    // Editing a date field changes `filters` but not this, so the panel never
    // shows one range under the labels of another.
    const [applied, setApplied] = useState(filters);
    // Only the newest request may write state: two overlapping fetches can
    // resolve out of order, and the slower, wider one would otherwise land
    // last and overwrite the window the user is looking at.
    const latest = useRef(0);

    const fetchStats = useCallback(async (range) => {
        const ticket = ++latest.current;
        setLoading(true);
        try {
            const response = await fetchApi(
                `/payment-methods/stats?start=${range.start}&end=${range.end}`
            );
            if (ticket !== latest.current) return;
            if (response.success) {
                setData(response.data);
                setApplied(range);
            }
        } catch (err) {
            if (ticket !== latest.current) return;
            notif.addNotification('Erreur', `Erreur: ${err}`, 'warning');
        } finally {
            if (ticket === latest.current) setLoading(false);
        }
    }, []);

    // Mount only. Each date keystroke used to rebuild fetchStats and refire
    // five queries — two aggregations, a count, a findOne and a distinct — so
    // picking two dates cost four round trips before « Actualiser » could add
    // a fifth that merely repeated them.
    useEffect(() => {
        fetchStats(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchStats]);

    // Stop the newest request from writing state after the page is gone.
    useEffect(() => () => { latest.current = -1; }, []);

    const pending = filters.start !== applied.start || filters.end !== applied.end;

    const handleExport = () => {
        if (!data || !data.byKind.length) return;

        const rows = [];
        data.byKind.forEach(group => {
            group.rows.forEach(r => {
                rows.push({
                    'Funnel': group.label,
                    'Moyen de paiement': labelOf(r.method),
                    'Nombre': r.count,
                    'Part (%)': r.share,
                    'Encaissés': r.taken,
                    'Frais annulation': r.fee,
                    // A number, not euros(): json_to_sheet types a string as
                    // a text cell, and SUM() over text returns 0.
                    'Net (EUR)': r.net / 100,
                });
            });
        });

        exportAsExcel(rows, `moyens_paiement_${isoDay(new Date())}`);
    };

    // Stacked bars, one segment per method, over every day in the window.
    //
    // The categories are walked from the selected range rather than taken from
    // the response: the aggregation only returns days that have rows, so a
    // quiet weekend — or the nine months before recording started — would
    // vanish and the remaining bars would read as consecutive days.
    const dailyChartData = React.useMemo(() => {
        if (!data) return null;

        const from = moment.tz(applied.start, PARIS);
        const to = moment.tz(applied.end, PARIS);
        if (!from.isValid() || !to.isValid() || to.isBefore(from)) return null;

        // A year of daily bars is unreadable; above that the chart is more
        // misleading than no chart, so the table stands alone.
        if (to.diff(from, 'days') > 366) return null;

        const dates = [];
        for (const d = from.clone(); !d.isAfter(to, 'day'); d.add(1, 'day')) {
            dates.push(d.format('YYYY-MM-DD'));
        }

        const counts = new Map();
        data.daily.forEach(d => counts.set(`${d.date}|${d.method}`, d.count));

        const methods = [...new Set(data.daily.map(d => d.method))].sort();
        if (!methods.length) return null;

        return {
            categories: dates,
            series: methods.map(method => ({
                name: labelOf(method),
                // Both consumed by SimpleChart; without them the chart drew
                // ECharts' default palette, so a reader matched the wrong
                // colour to the wrong method against the tables above.
                color: colorOf(method),
                stack: 'total',
                data: dates.map(date => counts.get(`${date}|${method}`) || 0),
            })),
        };
    }, [data, applied]);

    // Wallet share across all funnels, the single number people ask for.
    const walletShare = React.useMemo(() => {
        if (!data || !data.byKind.length) return null;

        let wallet = 0;
        let total = 0;
        data.byKind.forEach(group => {
            group.rows.forEach(r => {
                total += r.count;
                if (WALLETS.includes(r.method)) {
                    wallet += r.count;
                }
            });
        });

        if (!total) return null;
        return { wallet, total, pct: Math.round((wallet / total) * 1000) / 10 };
    }, [data]);

    const startedAt = data && data.recordingStartedAt
        ? moment.tz(data.recordingStartedAt, PARIS)
        : null;
    // A window opening before the first recorded charge shows a partial
    // picture, and the reason is invisible from the figures alone: the webhook
    // only started writing on the day it shipped. Without saying so, an empty
    // range reads as "nobody pays with a wallet".
    //
    // Compared in Paris against the window that was actually fetched. Parsing
    // the date as browser-local, or against the field the user is still
    // editing, put the banner a day out from the data on screen.
    const windowPrecedesData = startedAt
        && moment.tz(applied.start, PARIS).isBefore(startedAt, 'day');

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Moyens de paiement</h2>
                    <p className="text-muted">
                        Apple&nbsp;Pay / Google&nbsp;Pay contre une carte saisie.
                        Stripe n&apos;expose pas ce filtre : la donnée est copiée
                        depuis chaque charge par le webhook.
                    </p>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <div className="form-group">
                        <label>Date début</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.start}
                            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Date fin</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>&nbsp;</label>
                        <div>
                            <Button
                                bsStyle={pending ? 'primary' : 'default'}
                                onClick={() => fetchStats(filters)}
                                disabled={loading}
                            >
                                {loading ? 'Chargement…' : 'Actualiser'}
                            </Button>
                            {' '}
                            <Button onClick={handleExport} disabled={!data || loading}>
                                Export Excel
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {pending && data && (
                <Row>
                    <Col xs={12}>
                        <div className="alert alert-warning">
                            Les chiffres ci-dessous portent encore sur{' '}
                            {moment.tz(applied.start, PARIS).format('DD/MM/YYYY')} –{' '}
                            {moment.tz(applied.end, PARIS).format('DD/MM/YYYY')}.
                            Cliquez « Actualiser » pour appliquer les nouvelles dates.
                        </div>
                    </Col>
                </Row>
            )}

            {windowPrecedesData && (
                <Row>
                    <Col xs={12}>
                        <div className="alert alert-info">
                            L&apos;enregistrement a commencé le{' '}
                            <strong>{startedAt.format('DD/MM/YYYY')}</strong>.
                            Avant cette date il n&apos;y a pas de données — ce n&apos;est
                            pas une absence de paiements par wallet.
                        </div>
                    </Col>
                </Row>
            )}

            {data && data.currencies.length > 1 && (
                <Row>
                    <Col xs={12}>
                        <div className="alert alert-warning">
                            Plusieurs devises sur la période (
                            {data.currencies.join(', ').toUpperCase()}) : la colonne
                            « Net » les additionne, elle n&apos;est pas exploitable telle
                            quelle.
                        </div>
                    </Col>
                </Row>
            )}

            {walletShare && (
                <Row className="mb-4">
                    <Col md={4}>
                        <Panel>
                            <Panel.Body className="text-center">
                                <h3 style={{ margin: 0 }}>{walletShare.pct}%</h3>
                                <p className="text-muted" style={{ margin: 0 }}>
                                    des paiements via un wallet
                                    ({walletShare.wallet}/{walletShare.total})
                                </p>
                            </Panel.Body>
                        </Panel>
                    </Col>
                    <Col md={4}>
                        <Panel>
                            <Panel.Body className="text-center">
                                <h3 style={{ margin: 0 }}>{walletShare.total}</h3>
                                <p className="text-muted" style={{ margin: 0 }}>
                                    autorisations sur la période
                                </p>
                            </Panel.Body>
                        </Panel>
                    </Col>
                    <Col md={4}>
                        <Panel>
                            <Panel.Body className="text-center">
                                <h3 style={{ margin: 0 }}>{data.totalRows}</h3>
                                <p className="text-muted" style={{ margin: 0 }}>
                                    lignes enregistrées au total
                                </p>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {data && data.byKind.map(group => (
                <Row key={group.kind} className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                {group.label} — {group.total} autorisation
                                {group.total > 1 ? 's' : ''}
                            </Panel.Heading>
                            <Panel.Body>
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Moyen de paiement</th>
                                            <th className="text-right">Nombre</th>
                                            <th className="text-right">Part</th>
                                            <th className="text-right">Encaissés</th>
                                            <th className="text-right">Frais annul.</th>
                                            <th className="text-right">Net (€)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.rows.map(r => (
                                            <tr key={r.method}>
                                                <td>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        marginRight: 8,
                                                        background: colorOf(r.method),
                                                    }}
                                                    />
                                                    {labelOf(r.method)}
                                                </td>
                                                <td className="text-right">{r.count}</td>
                                                <td className="text-right">{r.share}%</td>
                                                <td className="text-right">{r.taken}</td>
                                                <td className="text-right">{r.fee}</td>
                                                <td className="text-right">{euros(r.net)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="text-muted" style={{ marginBottom: 0 }}>
                                    « Nombre » compte toutes les autorisations, y compris
                                    celles qu&apos;aucun médecin n&apos;a prises — c&apos;est
                                    la base de la part. « Encaissés » = capture totale ;
                                    « Frais annul. » = capture partielle, soit les 10 €
                                    d&apos;annulation. Les deux ne s&apos;additionnent pas.
                                </p>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            ))}

            {data && !data.byKind.length && (
                <Row>
                    <Col xs={12}>
                        <Panel>
                            <Panel.Body>
                                Aucun paiement enregistré sur cette période.
                                {data.totalRows > 0 && (
                                    <span>
                                        {' '}La collection contient {data.totalRows} ligne
                                        {data.totalRows > 1 ? 's' : ''} en dehors de la
                                        période sélectionnée.
                                    </span>
                                )}
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {dailyChartData && (
                <Row>
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>Par jour</Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    categories={dailyChartData.categories}
                                    series={dailyChartData.series}
                                    height={320}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}
        </Grid>
    );
};

export default PaymentMethods;
